/**
 * Block creation utilities for the Drawing module.
 *
 * Creates ODA block definitions from BlockConfig.
 * Blocks are created in the database's block table and can be
 * inserted as block references (inserts).
 */

import type { DRDrawingViewer } from "../DRDrawingViewer";
import type { DrawingModule } from "../types/DrawingWeb";
import type {
  BlockConfig,
  GeometryDataTyped as BlockGeometry,
  BlockAttribute,
} from "@/types/blocks.types";
import { deleteObj } from "../core/delete";
import { openAs } from "../core/oda";
import {
  OpenMode,
  HatchPatternType,
  HatchLoopType,
  HatchPatternTypeClosure,
} from "../core/constants";
import { calculateArcFrom3Points } from "../core/geometry-math";
/**
 * Cache of created block IDs by name.
 */
const blockCache = new Map<string, DrawingModule.OdDbObjectId>();

/**
 * Clear the block cache (e.g., when closing a file).
 */
export function clearBlockCache(): void {
  blockCache.clear();
}

/**
 * Create a block definition from a BlockConfig.
 * Returns the block table record ID.
 *
 * @param viewer The drawing viewer
 * @param config Block configuration
 * @param forceRecreate If true, recreates even if block exists
 */
export function createBlock(
  viewer: DRDrawingViewer,
  config: BlockConfig,
  forceRecreate = false,
): DrawingModule.OdDbObjectId | null {
  const module = viewer.module;
  const db = viewer.database;

  if (!db) {
    console.error("[BlockBuilder] No database available");
    return null;
  }

  // Check cache first
  if (!forceRecreate && blockCache.has(config.name)) {
    return blockCache.get(config.name) || null;
  }

  // Check if block already exists in database
  const existingId = findBlockByName(viewer, config.name);
  if (existingId && !forceRecreate) {
    blockCache.set(config.name, existingId);
    return existingId;
  }
  // Get block table
  const blockTableId = db.getBlockTableId();
  const blockTable = openAs(
    blockTableId,
    module.OdDbBlockTable,
    OpenMode.kForWrite,
  );
  let blockRecord: DrawingModule.OdDbBlockTableRecord;
  let blockRecordId: DrawingModule.OdDbObjectId;

  if (existingId) {
    // if block exists in table, empty it
    blockRecord = openAs(
      existingId,
      module.OdDbBlockTableRecord,
      OpenMode.kForWrite,
    );
    blockRecordId = existingId;

    // Erase all existing entities before re-adding
    const iter = blockRecord.newIterator(true, false, false);
    iter.start(true, false);
    while (!iter.done()) {
      const entity = iter.entity(OpenMode.kForWrite, false);
      if (entity) {
        entity.erase(true);
        deleteObj(entity);
      }
      iter.step(true, false);
    }
    deleteObj(iter);
  } else {
    // create object
    blockRecord = module.OdDbBlockTableRecord.createObject();

    const nameStr = new module.OdString(config.name);
    blockRecord.setName(nameStr);
    deleteObj(nameStr);

    // Add block record to block table
    blockRecordId = blockTable.add(blockRecord);
  }

  // Add geometries to block
  appendGeometriesToBlock(viewer, blockRecord, config.geometries, config.scale);

  // Add attribute definitions to block
  if (config.attributes && config.attributes.length > 0) {
    appendAttributeDefinitionsToBlock(
      viewer,
      blockRecord,
      config.attributes,
      config.scale,
    );
  }

  deleteObj(blockRecord);
  deleteObj(blockTable);

  blockCache.set(config.name, blockRecordId);

  return blockRecordId;
}

/**
 * Find a block by its full name.
 */
function findBlockByName(
  viewer: DRDrawingViewer,
  fullName: string,
): DrawingModule.OdDbObjectId | null {
  const module = viewer.module;
  const db = viewer.database;

  if (!db) return null;

  const blockTableId = db.getBlockTableId();
  const blockTable = openAs(
    blockTableId,
    module.OdDbBlockTable,
    OpenMode.kForRead,
  );

  const nameStr = new module.OdString(fullName);
  const hasBlock = blockTable.has(nameStr);

  if (hasBlock) {
    // Use getAt with OpenMode to get the record, then get its objectId
    const blockRecord = blockTable.getAt(nameStr, OpenMode.kForRead, false);
    const blockId = blockRecord.objectId();
    deleteObj(nameStr);
    deleteObj(blockTable);
    deleteObj(blockRecord);
    return blockId;
  }

  deleteObj(nameStr);
  deleteObj(blockTable);
  return null;
}

/**
 * Check if a block exists by config name.
 */
export function blockExists(viewer: DRDrawingViewer, name: string): boolean {
  return findBlockByName(viewer, name) !== null;
}

/**
 * Get block ID by config name.
 */
export function getBlockId(
  viewer: DRDrawingViewer,
  name: string,
): DrawingModule.OdDbObjectId | null {
  // Check cache first
  if (blockCache.has(name)) {
    return blockCache.get(name)!;
  }

  const blockId = findBlockByName(viewer, name);

  if (blockId) {
    blockCache.set(name, blockId);
  }

  return blockId;
}

/**
 * Append geometries to a block table record.
 */
function appendGeometriesToBlock(
  viewer: DRDrawingViewer,
  blockRecord: DrawingModule.OdDbBlockTableRecord,
  geometries: BlockGeometry[],
  scale: number,
): void {
  const module = viewer.module;

  for (const geom of geometries) {
    switch (geom.type) {
      case "polyline":
        appendPolyline(module, blockRecord, geom.points, scale, geom.closed);
        break;
      case "circle":
        appendCircle(module, blockRecord, geom, scale);
        break;
      case "text":
        appendText(module, blockRecord, geom, scale);
        break;
      case "ellipse":
        appendEllipse(module, blockRecord, geom, scale);
        break;
      case "circle-arc":
        appendArc(module, blockRecord, geom, scale);
        break;
      case "polygon":
        appendPolyline(module, blockRecord, geom.corners, scale, true);
        break;
    }
  }
}

/**
 * Append a polyline to a block.
 */
function appendPolyline(
  module: DRDrawingViewer["module"],
  blockRecord: DrawingModule.OdDbBlockTableRecord,
  points: [number, number, number][],
  scale: number,
  closed = false,
): void {
  const polyline = module.OdDbPolyline.createObject();

  for (let i = 0; i < points.length; i++) {
    const [x, y] = points[i];
    const point2d = new module.OdGePoint2d(x * scale, y * scale);
    polyline.addVertexAt(i, point2d, 0, 0, 0, 0);
    deleteObj(point2d);
  }

  if (closed) {
    polyline.setClosed(true);
  }

  // kByBlock so the entity inherits color from the block reference
  const color = new module.OdCmColor();
  color.setColorMethod(193); // kByBlock
  polyline.setColor(color, true);
  deleteObj(color);

  blockRecord.appendOdDbEntity(polyline);
  deleteObj(polyline);
}

/**
 * Append a circle to a block.
 * If the circle has `filled: true`, creates a solid hatch as well.
 */
function appendCircle(
  module: DRDrawingViewer["module"],
  blockRecord: DrawingModule.OdDbBlockTableRecord,
  geom: BlockGeometry & { type: "circle" },
  scale: number,
): void {
  const circle = module.OdDbCircle.createObject();

  const center = new module.OdGePoint3d(
    geom.center[0] * scale,
    geom.center[1] * scale,
    geom.center[2] * scale,
  );
  circle.setCenter(center);
  circle.setRadius(geom.radius * scale);

  deleteObj(center);

  // kByBlock so the entity inherits color from the block reference
  const color = new module.OdCmColor();
  color.setColorMethod(193); // kByBlock
  circle.setColor(color, true);
  deleteObj(color);

  // Append the circle and keep its ID as hatch boundary
  const circleId = blockRecord.appendOdDbEntity(circle);
  deleteObj(circle);

  // If filled, create a solid hatch inside the circle
  if (geom.filled) {
    const hatch = module.OdDbHatch.createObject();

    // Append to block first — gives DB context before setDatabaseDefaults
    blockRecord.appendOdDbEntity(hatch);

    const db = blockRecord.database();
    hatch.setDatabaseDefaults(db, true);

    const hatchColor = new module.OdCmColor();
    hatchColor.setColorMethod(193); // ByBlock — inherits color from block reference
    hatch.setColor(hatchColor, true);
    deleteObj(hatchColor);

    hatch.setAssociative(false);

    const patternName = new module.OdString("SOLID");
    hatch.setPattern(HatchPatternType.kPreDefined as any, patternName, 0);
    deleteObj(patternName);

    hatch.setHatchStyle(0); // kNormal = 0

    // Use circle's objectId as boundary — same pattern as createHatchFromEntity
    const ids = new module.OdDbObjectIdArray();
    ids.push_back(circleId);
    hatch.appendLoop2(
      HatchLoopType.kExternal as any,
      ids,
      HatchPatternTypeClosure.kSolidClosure,
    );
    deleteObj(ids);

    hatch.evaluateHatch(true);
    deleteObj(hatch);
  }
}

/**
 * Append text to a block.
 */
function appendText(
  module: DRDrawingViewer["module"],
  blockRecord: DrawingModule.OdDbBlockTableRecord,
  geom: BlockGeometry & { type: "text" },
  scale: number,
): void {
  const text = module.OdDbText.createObject();

  const position = new module.OdGePoint3d(
    geom.refpoint[0] * scale,
    geom.refpoint[1] * scale,
    geom.refpoint[2] * scale,
  );
  text.setPosition(position);

  const textStr = new module.OdString(geom.message);
  text.setTextString(textStr);

  const height = (geom.height ?? 0.5) * scale;
  text.setHeight(height);

  deleteObj(position);
  deleteObj(textStr);

  // kByBlock so the entity inherits color from the block reference
  const color = new module.OdCmColor();
  color.setColorMethod(193); // kByBlock
  text.setColor(color, true);
  deleteObj(color);

  blockRecord.appendOdDbEntity(text);
  deleteObj(text);
}

/**
 * Append an ellipse to a block.
 */
function appendEllipse(
  module: DRDrawingViewer["module"],
  blockRecord: DrawingModule.OdDbBlockTableRecord,
  geom: BlockGeometry & { type: "ellipse" },
  scale: number,
): void {
  const ellipse = module.OdDbEllipse.createObject();

  const centerPt = new module.OdGePoint3d(
    geom.center[0] * scale,
    geom.center[1] * scale,
    geom.center[2] * scale,
  );
  const normal = new module.OdGeVector3d(0, 0, 1);
  const majorAxisVec = new module.OdGeVector3d(
    (geom.majorPoint[0] - geom.center[0]) * scale,
    (geom.majorPoint[1] - geom.center[1]) * scale,
    (geom.majorPoint[2] - geom.center[2]) * scale,
  );

  const majorRadius = Math.sqrt(
    majorAxisVec.x * majorAxisVec.x +
      majorAxisVec.y * majorAxisVec.y +
      majorAxisVec.z * majorAxisVec.z,
  );
  const minorDist =
    Math.sqrt(
      Math.pow(geom.minorPoint[0] - geom.center[0], 2) +
        Math.pow(geom.minorPoint[1] - geom.center[1], 2) +
        Math.pow(geom.minorPoint[2] - geom.center[2], 2),
    ) * scale;
  const radiusRatio = majorRadius > 0 ? minorDist / majorRadius : 1;

  ellipse.set(centerPt, normal, majorAxisVec, radiusRatio, 0, Math.PI * 2);

  deleteObj(centerPt);
  deleteObj(normal);
  deleteObj(majorAxisVec);

  // kByBlock so the entity inherits color from the block reference
  const color = new module.OdCmColor();
  color.setColorMethod(193); // kByBlock
  ellipse.setColor(color, true);
  deleteObj(color);

  blockRecord.appendOdDbEntity(ellipse);
  deleteObj(ellipse);
}

/**
 * Append an arc (3 points) to a block.
 */
function appendArc(
  module: DRDrawingViewer["module"],
  blockRecord: DrawingModule.OdDbBlockTableRecord,
  geom: BlockGeometry & { type: "circle-arc" },
  scale: number,
): void {
  const scaledStart: [number, number, number] = [
    geom.start[0] * scale,
    geom.start[1] * scale,
    geom.start[2] * scale,
  ];
  const scaledMiddle: [number, number, number] = [
    geom.middle[0] * scale,
    geom.middle[1] * scale,
    geom.middle[2] * scale,
  ];
  const scaledEnd: [number, number, number] = [
    geom.end[0] * scale,
    geom.end[1] * scale,
    geom.end[2] * scale,
  ];

  const arcParams = calculateArcFrom3Points(
    scaledStart,
    scaledMiddle,
    scaledEnd,
  );
  if (!arcParams) return;

  const arc = module.OdDbArc.createObject();

  const center = new module.OdGePoint3d(
    arcParams.center.x,
    arcParams.center.y,
    0,
  );
  arc.setCenter(center);
  arc.setRadius(arcParams.radius);
  arc.setStartAngle(arcParams.startAngle);
  arc.setEndAngle(arcParams.endAngle);

  deleteObj(center);

  // kByBlock so the entity inherits color from the block reference
  const color = new module.OdCmColor();
  color.setColorMethod(193); // kByBlock
  arc.setColor(color, true);
  deleteObj(color);

  blockRecord.appendOdDbEntity(arc);
  deleteObj(arc);
}

/**
 * Append attribute definitions to a block table record.
 */
function appendAttributeDefinitionsToBlock(
  viewer: DRDrawingViewer,
  blockRecord: DrawingModule.OdDbBlockTableRecord,
  attributes: BlockAttribute[],
  scale: number,
): void {
  for (const attr of attributes) {
    appendAttributeDefinition(viewer, blockRecord, attr, scale);
  }
}

/**
 * Append an attribute definition to a block.
 * Attribute definitions are templates for customizable text per block reference.
 */
function appendAttributeDefinition(
  viewer: DRDrawingViewer,
  blockRecord: DrawingModule.OdDbBlockTableRecord,
  attr: BlockAttribute,
  scale: number,
): void {
  const module = viewer.module;
  const db = viewer.database;
  if (!db) return;

  const attrDef = module.OdDbAttributeDefinition.createObject();
  attrDef.setDatabaseDefaults(db, true);

  // kByBlock so the entity inherits color from the block reference
  const color = new module.OdCmColor();
  color.setColorMethod(193); // kByBlock
  attrDef.setColor(color, true);
  deleteObj(color);

  const layerZeroName = new module.OdString("0");
  attrDef.setLayer(layerZeroName, false, true);
  deleteObj(layerZeroName);

  // Set tag (identifier for this attribute)
  const tagStr = new module.OdString(attr.tag);
  attrDef.setTag(tagStr);
  deleteObj(tagStr);

  // Set prompt (shown when inserting block)
  if (attr.prompt) {
    const promptStr = new module.OdString(attr.prompt);
    attrDef.setPrompt(promptStr);
    deleteObj(promptStr);
  }

  // Set text height first (needed for alignment calculations)
  const height = (attr.height ?? 1.5) * scale;
  attrDef.setHeight(height);

  // Set alignment modes before setting position
  if (attr.horizontalMode !== undefined) {
    attrDef.setHorizontalMode(attr.horizontalMode);
  }
  if (attr.verticalMode !== undefined) {
    attrDef.setVerticalMode(attr.verticalMode);
  }

  // Set position - for center alignment, use alignmentPoint
  const position = new module.OdGePoint3d(
    attr.position[0] * scale,
    attr.position[1] * scale,
    attr.position[2] * scale,
  );
  attrDef.setAlignmentPoint(position);
  deleteObj(position);

  // Set default text value
  if (attr.defaultValue) {
    const defaultStr = new module.OdString(attr.defaultValue);
    attrDef.setTextString(defaultStr);
    deleteObj(defaultStr);
  }

  // Set visibility
  if (attr.invisible) {
    attrDef.setInvisible(true);
  }

  // Set constant flag
  if (attr.constant) {
    attrDef.setConstant(true);
  }

  blockRecord.appendOdDbEntity(attrDef);
  deleteObj(attrDef);
}
